import React, { useEffect } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Controller, FormProvider } from "react-hook-form";
import Select from "react-select";

import classNames from "classnames";

import AdminSettings from "./AdminSettings";

import { useTeamContext } from "../contexts/Team";
import useFormHandlers from "../hooks/useFormHandler";
import getTeamAgeLevel from "../lib/getTeamAgeLevel";

import FileUpload from "../../../../../components/FileUpload";
import Loading from "../../../../../components/Loading";
import {
    selectSetting,
    selectUmpires,
} from "../../../../../features/global/globalSlice";
import { selectUsers } from "../../../../../features/user/userSlice";
import { useAppSelector } from "../../../../../hooks/reduxHooks";
import {
    AUTH_LEVELS,
    TEAM_LOGO_URL,
    VALID_FILE_EXTENSIONS,
} from "../../../../../lib/globals";
import { displayFormAlert } from "../../../../../lib/utils";

const TeamForm = (): React.JSX.Element => {
    const allowedExtensions = VALID_FILE_EXTENSIONS.image.slice();
    const user = useAppSelector(state => state.auth.user);
    const users = useAppSelector(selectUsers);
    const currentYear = useAppSelector(state =>
        selectSetting(state, "majorybl_current_league_year"),
    );
    const umpires = useAppSelector(selectUmpires);

    const {
        team,
        fileList,
        setFileList,
        phone,
        setPhone,
        formMethods: methods,
        asyncStatus,
    } = useTeamContext();
    const { handleSubmit } = useFormHandlers();
    const teamAgeLevel = getTeamAgeLevel(team, currentYear);

    const umpireOptions = umpires.map(u => {
        return {
            value: parseInt(u.id.toString()),
            label: u.name,
        };
    });

    useEffect(() => {
        const subscription = methods.watch((value, { name, type }) => {
            if (name === "coach" && type == "change") {
                setPhone(
                    users.find(u => u.id === value.coach?.value)!.meta
                        .phoneNumbers.primary,
                );
            }
        });
        return () => subscription.unsubscribe();
    }, [methods.watch]);

    return (
        <section className="teamInfo">
            <FormProvider {...methods}>
                <Form
                    onSubmit={methods.handleSubmit(handleSubmit)}
                    id="clubhouseTeamInfoForm"
                    data-testid="clubhouseTeamInfoForm"
                >
                    <Row>
                        <Col>
                            <Form.Group controlId="name">
                                <Form.Label>Team Name *</Form.Label>
                                <Form.Control
                                    type="text"
                                    {...methods.register("name", {
                                        required: "Team Name is required",
                                    })}
                                    aria-invalid={
                                        methods.formState.errors.name
                                            ? "true"
                                            : "false"
                                    }
                                />
                                <Form.Text>
                                    Note: Please do not include age level or
                                    league designation in your team's name
                                </Form.Text>
                                {methods.formState.errors.name &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.name
                                            .message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group controlId="coach" data-testid="coach">
                                <Form.Label id="coach">Team Contact</Form.Label>
                                <Controller
                                    name="coach"
                                    control={methods.control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            aria-labelledby="coach"
                                            isSearchable={true}
                                            options={users.map(u => {
                                                return {
                                                    value: parseInt(
                                                        u.id.toString(),
                                                    ),
                                                    label: `${u.meta.firstName} ${u.meta.lastName} (${u.email})`,
                                                };
                                            })}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Label>Team Logo</Form.Label>
                            {team?.logo && team.logo !== "" && (
                                <img
                                    className="teamLogo"
                                    src={`${TEAM_LOGO_URL}${team.logo}`}
                                />
                            )}
                            <Form.Group controlId="files">
                                <FileUpload
                                    maxFileCount={1}
                                    allowedFileExtensions={allowedExtensions}
                                    maxFileSize={1024000}
                                    fileList={fileList}
                                    setFileList={setFileList}
                                />
                                <Form.Label className="small">
                                    Accepted file types: jpg, jpeg, png
                                    <br />
                                    Max file size: 1 MB
                                </Form.Label>
                                <p className="small">
                                    Upload a new logo to replace current one
                                </p>
                            </Form.Group>
                            {methods.formState.errors.files &&
                                displayFormAlert({
                                    level: "danger",
                                    text: methods.formState.errors.files
                                        .message as string,
                                })}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Label>Contact Number</Form.Label>
                            <p>{phone}</p>
                        </Col>
                    </Row>
                    {AUTH_LEVELS.leaguemanagement.includes(user!.userLevel) && (
                        <AdminSettings />
                    )}
                    <Row>
                        <Col>
                            <Form.Label>Team Age Level</Form.Label>
                            <p>{teamAgeLevel}</p>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId={`bannedUmpire1`}>
                                <Form.Label>Banned Umpire 1</Form.Label>
                                <Controller
                                    name={`bannedUmpire1`}
                                    control={methods.control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={true}
                                            isClearable={true}
                                            options={umpireOptions}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="bannedUmpire2">
                                <Form.Label>Banned Umpire 2</Form.Label>
                                <Controller
                                    name="bannedUmpire2"
                                    control={methods.control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={true}
                                            isClearable={true}
                                            options={umpireOptions}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col
                            className={classNames(
                                "d-flex",
                                "align-items-baseline",
                            )}
                        >
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={asyncStatus === "pending"}
                            >
                                Update Information
                            </Button>
                            {asyncStatus === "pending" && (
                                <Loading className="mx-3 w-auto" />
                            )}
                        </Col>
                    </Row>
                </Form>
            </FormProvider>
        </section>
    );
};

export default TeamForm;
